import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { copyIcon, newChatIcon, rewindIcon, editIcon, regenerateIcon } from '../../assets/icons';
import styles from './MessageMenuMobile.module.css';
import { MessageType, MessageMenuProps } from '../../types/message';

interface MessageMenuMobileProps extends Omit<MessageMenuProps, 'position'> {
  // MessageMenuMobile doesn't need position prop
}

const MessageMenuMobile: React.FC<MessageMenuMobileProps> = ({ 
  onClose, 
  onCopy,
  onNewChat,
  onRewind,
  onEdit,
  onRegenerate,
  isLastMessage,
  isCharacterMessage,
  message,
  isRegenerating = false
}) => {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const popupRef = useRef<HTMLDivElement>(null);

  // Animation effect when component mounts
  useEffect(() => {
    // Small delay to ensure DOM is ready for the animation
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 10);
    
    // Prevent body scrolling when popup is open
    document.body.style.overflow = 'hidden';
    
    return () => {
      clearTimeout(timer);
      document.body.style.overflow = '';
    };
  }, []);

  // Handle backdrop click to close the popup
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // Handle close with animation
  const handleClose = (): void => {
    setIsVisible(false);
    // Wait for animation to complete before unmounting
    setTimeout(() => {
      onClose();
    }, 300);
  };

  // Handle action buttons
  const handleCopy = (): void => {
    onCopy(message.content);
    handleClose();
  };

  const handleNewChat = (): void => {
    onNewChat();
    handleClose();
  };

  const handleEdit = (): void => {
    onEdit();
    handleClose();
  };

  const handleRewind = (): void => {
    onRewind();
    handleClose();
  };

  const handleRegenerate = (): void => {
    if (!isRegenerating) {
      onRegenerate();
      handleClose();
    }
  };

  // Create portal content
  const portalContent = (
    <div 
      className={`${styles.backdrop} ${isVisible ? styles.backdropVisible : ''}`}
      onClick={handleBackdropClick}
    >
      <div 
        ref={popupRef}
        className={`${styles.popupContainer} ${isVisible ? styles.visible : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.handle}></div>
        
        <button className={styles.actionButton} onClick={handleCopy}>
          <div className={styles.iconContainer}>
            <img src={copyIcon} alt="" className={styles.menuIcon} />
          </div>
          <span>Copy</span>
        </button>
        
        <button className={styles.actionButton} onClick={handleNewChat}>
          <div className={styles.iconContainer}>
            <img src={newChatIcon} alt="" className={styles.menuIcon} />
          </div>
          <span>New chat from here</span>
        </button>
        
        {/* Show Rewind for non-latest messages or user messages */}
        {(!isLastMessage || !isCharacterMessage) && (
          <button className={styles.actionButton} onClick={handleRewind}>
            <div className={styles.iconContainer}>
              <img src={rewindIcon} alt="" className={styles.menuIcon} />
            </div>
            <span>Rewind to here</span>
          </button>
        )}
        
        <button className={styles.actionButton} onClick={handleEdit}>
          <div className={styles.iconContainer}>
            <img src={editIcon} alt="" className={styles.menuIcon} />
          </div>
          <span>Edit message</span>
        </button>
        
        {/* Show Regenerate only for latest character messages */}
        {isLastMessage && isCharacterMessage && (
          <button 
            className={`${styles.actionButton} ${isRegenerating ? styles.disabled : ''}`} 
            onClick={handleRegenerate}
            disabled={isRegenerating}
          >
            <div className={styles.iconContainer}>
              <img src={regenerateIcon} alt="" className={`${styles.menuIcon} ${isRegenerating ? styles.disabled : ''}`} />
            </div>
            <span>{isRegenerating ? 'Regenerating...' : 'Regenerate message'}</span>
          </button>
        )}
      </div>
    </div>
  );

  // Use createPortal to render the popup at the end of the document body
  return createPortal(portalContent, document.body);
};

export default MessageMenuMobile; 